import React, {useEffect} from 'react';
import {setLocalStorageSilent} from '../forecast-app/shared/util/LocalStorageUtil';
import styled from 'styled-components';
import {graphql, createFragmentContainer} from 'react-relay';
import {withRouter, useHistory} from 'react-router-dom';
import {Icon} from '@forecast-it/design-system';
import Util from '../forecast-app/shared/util/util';
import {MODAL_TYPE, showModal} from '../forecast-app/shared/components/modals/generic_modal_conductor';
import {TABS} from './project/project_settings/ProjectSettingsUtil';
import {hasPermission, hasSomePermission} from '../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../Permissions';
import {TIERS} from '../constants';
import Tracking from '../tracking';
import {trackEvent} from '../tracking/amplitude/TrackingV2';
import {startCase} from 'lodash';
import {useTrackPage} from '../tracking/amplitude/hooks/useTrackPage';

const TrialGuideContainer = styled.div`
	display: grid;
	@media (min-width: 1800px) {
		max-width: 1432px;
		align-self: center;
		grid-template-columns: 33% 33% 33%;
		grid-template-rows: auto 1fr;
		grid-template-areas:
			'title empty1 empty2'
			'welcome section1 section2';
	}
	@media (max-width: 1799px) {
		grid-template-columns: 33% 33% 33%;
		grid-template-rows: auto 1fr;
		grid-template-areas:
			'title empty1 empty2'
			'welcome section1 section2';
	}
	@media (max-width: 1327px) {
		grid-template-columns: 50% 50%;
		grid-template-rows: auto auto 1fr;
		grid-template-areas:
			'title empty1'
			'welcome empty2'
			'section1 section2';
	}
	@media (max-width: 1023px) {
		grid-template-columns: 1fr;
		grid-template-rows: auto auto auto auto auto 1fr;
		grid-template-areas:
			'title'
			'welcome'
			'section1'
			'section2'
			'empty1'
			'empty2';
	}
	height: 100%;
	padding: 0px 60px;
	overflow: auto;
`;

const Column = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	border-left: 1px solid #f5f5f5;
	padding: 0px 20px;
`;

const WelcomeSection = styled(Column)`
	@media (min-width: 1328px) {
		border-left: none;
	}
	grid-area: welcome;
	padding-bottom: 16px;
`;

const TitleSection = styled(Column)`
	@media (min-width: 1328px) {
		border-left: none;
	}
	grid-area: title;
	white-space: nowrap;
	overflow: visible;
	font-weight: 500;
	font-size: 32px;
	line-height: 48px;
	color: #393946;
	padding-top: 36px;
`;

const MainDescription = styled.div`
	@media (min-width: 1800px) {
		width: 384px;
	}
	@media (max-width: 1799px) {
		width: 320px;
	}
	@media (max-width: 1327px) {
		width: 642px;
	}
	@media (max-width: 1023px) {
		width: 440px;
	}
	grid-column-start: 1;
	grid-row-start: 1;
	font-weight: 400;
	font-size: 16px;
	line-height: 24px;
	color: #393946;
	p {
		margin-top: 0px;
		margin-bottom: 8px;
		a {
			font-weight: 700;
			color: #661bb1;
			cursor: pointer;
			text-decoration: none;
		}
	}
	z-index: 1;
`;

const GuideSection1 = styled(Column)`
	grid-area: section1;
`;
const GuideSection2 = styled(Column)`
	grid-area: section2;
`;
const EmptySection1 = styled(Column)`
	grid-area: empty1;
	z-index: -1;
`;
const EmptySection2 = styled(Column)`
	grid-area: empty2;
	z-index: -1;
`;

const SectionOuterContainer = styled.div`
	display: flex;
	flex-direction: row;
	padding-bottom: 16px;
	svg {
		width: 32px;
		height: 32px;
		flex: 32px 0 0;
		filter: invert(90%) sepia(4%) saturate(7331%) hue-rotate(330deg) brightness(106%) contrast(101%);
	}
`;

const SectionInnerContainer = styled.div`
	display: flex;
	flex-direction: column;
	margin-left: 16px;
`;
const SectionTitle = styled.div`
	font-weight: 700;
	font-size: 14px;
	line-height: 24px;
	color: #393946;
	margin-bottom: 8px;
`;
const SectionText = styled.div`
	font-weight: 400;
	font-size: 14px;
	line-height: 20px;
	color: #393946;
	a {
		font-weight: 700;
		color: #661bb1;
		cursor: pointer;
		text-decoration: none;
	}
`;

const TrialGuide = ({viewer}) => {
	// const intl = useIntl();
	// const [value, setValue] = useState(0);
	const history = useHistory();

	const handleCreateProject = () => {
		showModal({
			type: MODAL_TYPE.NEW_PROJECT_V2,
		});
	};

	const setFinancialTab = () => {
		setLocalStorageSilent('project_settings_tab', TABS.FINANCIALS.key);
	};

	const setRevRecTab = () => {
		setLocalStorageSilent('project-budget-v3-last-selected-tab', 'project_budget.revenue_recognition');
	};

	const link = (text, href, condition = true, onClick, target = '_self') => {
		return condition ? (
			<a
				href={href || undefined}
				onMouseUp={() => trackEvent(`Trial Guide ${startCase(text)}`, 'Clicked')} // Mouse up instead of click to capture middle click
				onClick={() => {
					Tracking.trackEvent(`Trial guide_${text.toLowerCase()} clicked`);
					onClick && onClick();
				}}
				target={target}
			>
				{text}
			</a>
		) : (
			text
		);
	};

	// Ids. default to NULL
	const projectId = viewer.projects.edges?.[0]?.node.companyProjectId || null;
	const sprintProjectId = viewer.projects.edges.find(p => p.node.sprintTimeBox)?.node.companyProjectId || null;
	const novaFixedPriceProjectId =
		viewer.projects.edges.find(p => !p.node.isInProjectGroup && p.node.budgetType === 'FIXED_PRICE_V2')?.node
			.companyProjectId || null;
	const novaProjectId =
		novaFixedPriceProjectId ||
		viewer.projects.edges.find(p => !p.node.isInProjectGroup && p.node.budgetType !== 'RETAINER')?.node.companyProjectId ||
		null;
	const fixedPriceProjectId =
		viewer.projects.edges.find(p => p.node.budgetType === 'FIXED_PRICE_V2')?.node.companyProjectId || null;
	const baselineProjectId = viewer.projects.edges.find(p => p.node.useBaseline)?.node.companyProjectId || null;
	const taskId = viewer.tasks.edges?.[0]?.node.companyTaskId || null;
	const utilizationReportId =
		viewer.company.allSavedReports.edges.find(report => report.node.type === 'Utilization Report')?.node.id || null;
	const pprId =
		viewer.company.allSavedReports.edges.find(report => report.node.type === 'Project Portfolio Report')?.node.id || null;
	// const timeRegReportId = viewer.company.allSavedReports.edges.find(node => node.type === 'Time Registered')?.node.id || null;
	// const taskReportId = viewer.company.allSavedReports.edges.find(node => node.type === 'Task report')?.node.id || null;

	const hasFinance = Util.hasFinance();
	const isAdmin = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);
	const canCreateProject = hasPermission(PERMISSION_TYPE.PROJECTS_CREATE);
	const canUpdateProjects = hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE);
	const canSeeReports = hasPermission(PERMISSION_TYPE.INSIGHTS_READ);
	const canSeeFinancials = hasSomePermission([
		PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION,
		PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION_REVENUE,
	]);
	const canCreateInvoices = hasPermission(PERMISSION_TYPE.INVOICE_CREATE);

	useTrackPage('Trial Guide');

	useEffect(() => {
		Tracking.trackEvent('Trial guide page viewed');
		// If no feature flag or trial ended, redirect
		if (viewer.company.tier !== TIERS.TRIAL || hasPermission(PERMISSION_TYPE.IS_CLIENT_USER)) {
			history.push('/');
		}
	}, []);

	return (
		<TrialGuideContainer>
			<TitleSection>Welcome to Forecast</TitleSection>
			<WelcomeSection>
				<MainDescription>
					<p>
						We’ve set up some example projects for you to get started. Explore how Forecast helps achieve your goals
						by following the suggested activities.
					</p>
					<p>
						If you prefer a personalized demo or just want to talk to someone about intelligent work management,{' '}
						{link('drop us a line.', 'mailto:sales@forecast.app')}
					</p>
				</MainDescription>
			</WelcomeSection>
			<GuideSection1>
				<SectionOuterContainer>
					<Icon icon={'project'} size={'l'} />
					<SectionInnerContainer>
						<SectionTitle>Manage projects</SectionTitle>
						<SectionText>
							Get an overview of all your {link('projects', '/projects')}, manage individual project{' '}
							{link('scope', `/project/P-${projectId}/scoping`, projectId)},{' '}
							{link('workflow', `/project/P-${projectId}/workflow`, projectId)},{' '}
							{link('backlog', `/project/P-${sprintProjectId}/sprints`, sprintProjectId)}, or{' '}
							{link('timelines', `/project/P-${projectId}/schedule`, projectId)}. Manage project{' '}
							{link('status and risks', `/project/P-${novaProjectId}/nova-insights`, novaProjectId)}.
						</SectionText>
					</SectionInnerContainer>
				</SectionOuterContainer>
				<SectionOuterContainer>
					<Icon icon={'todo'} size={'l'} />
					<SectionInnerContainer>
						<SectionTitle>Set up a project</SectionTitle>
						<SectionText>
							Get started by creating a {link('new project', '', canCreateProject, handleCreateProject)} or{' '}
							{link('change a demo project', `/project/P-${projectId}/workflow`, projectId)} to suit you.
						</SectionText>
					</SectionInnerContainer>
				</SectionOuterContainer>

				<SectionOuterContainer>
					<Icon icon={'report'} size={'l'} />
					<SectionInnerContainer>
						<SectionTitle>Reporting</SectionTitle>
						<SectionText>
							See reports on{' '}
							{link(
								'utilization',
								`/saved-report/utilization-report/${utilizationReportId}`,
								canSeeReports && utilizationReportId
							)}
							, {link('time registration', `/reports/new/step1`, canSeeReports)},{' '}
							{link('tasks', `/reports/new/step1`, canSeeReports)} and{' '}
							{link(
								'project portfolios',
								`/saved-report/project-portfolio-report/${pprId}`,
								canSeeReports && pprId
							)}
							. Or get a demo of our{' '}
							{link(
								'Advanced Analytics',
								'mailto:sales@forecast.app?subject=Requesting Advanced Analytics Demo',
								true,
								'_blank'
							)}
							.
						</SectionText>
					</SectionInnerContainer>
				</SectionOuterContainer>

				<SectionOuterContainer>
					<Icon icon={'client'} size={'l'} />
					<SectionInnerContainer>
						<SectionTitle>Your work</SectionTitle>
						<SectionText>
							Get an overview of all {link('tasks assigned', '/')} to you, make updates to a{' '}
							{link('task', `/T${taskId}`)}, {link('create expenses', '/expenses#newexpense', hasFinance)} and{' '}
							{link('register time', '/timesheets')}.
						</SectionText>
					</SectionInnerContainer>
				</SectionOuterContainer>
			</GuideSection1>
			<GuideSection2>
				<SectionOuterContainer>
					<Icon icon={'calendar'} size={'l'} />
					<SectionInnerContainer>
						<SectionTitle>Resource management</SectionTitle>
						<SectionText>
							Organise people into {link('teams', '/settings/teams', isAdmin)} and{' '}
							{link('departments', '/settings/departments', isAdmin)}. Add{' '}
							{link('skills', '/settings/skills', isAdmin)} for better resource management.{' '}
							{link('Schedule people', '/scheduling#people')} to projects and tasks, to optimise utilization.
						</SectionText>
					</SectionInnerContainer>
				</SectionOuterContainer>
				<SectionOuterContainer>
					<Icon icon={'expense'} size={'l'} />
					<SectionInnerContainer>
						<SectionTitle>Project financials</SectionTitle>
						<SectionText>
							View the {link('project baseline', `/project/P-${baselineProjectId}/baseline`, baselineProjectId)}{' '}
							when you are scoping a project, set a{' '}
							{link(
								'budget type',
								`/project/P-${projectId}/settings`,
								canUpdateProjects && projectId,
								setFinancialTab
							)}{' '}
							and follow up on the{' '}
							{link('project budget', `/project/P-${projectId}/budget`, canSeeFinancials && projectId)} as you
							progress.{' '}
							{link(
								'Recognize revenue',
								`/project/P-${fixedPriceProjectId}/budget`,
								canSeeFinancials && fixedPriceProjectId,
								setRevRecTab
							)}{' '}
							either automatically or manually.
						</SectionText>
					</SectionInnerContainer>
				</SectionOuterContainer>
				<SectionOuterContainer>
					<Icon icon={'watcher'} size={'l'} />
					<SectionInnerContainer>
						<SectionTitle>Portfolio financials and management</SectionTitle>
						<SectionText>
							See {link('all projects and their progress', '/scheduling#projects')}. Configure your financials by
							setting up {link('rate cards', '/settings/finance', isAdmin)}, add{' '}
							{link('internal cost', '/settings/people', isAdmin)} for people,{' '}
							{link('send invoices', '/invoicing/overview', canCreateInvoices)}, and view the{' '}
							{link(
								'financial performance',
								`/saved-report/project-portfolio-report/${pprId}`,
								canSeeReports && pprId
							)}{' '}
							of your project portfolio.
						</SectionText>
					</SectionInnerContainer>
				</SectionOuterContainer>
			</GuideSection2>
			<EmptySection1></EmptySection1>
			<EmptySection2></EmptySection2>
		</TrialGuideContainer>
	);
};

const TrialGuideQuery = graphql`
	query TrialGuide_Query {
		viewer {
			actualPersonId
			component(name: "trial_guide")
			...TrialGuide_viewer
		}
	}
`;

export {TrialGuideQuery};

export default withRouter(
	createFragmentContainer(TrialGuide, {
		viewer: graphql`
			fragment TrialGuide_viewer on Viewer {
				id
				company {
					id
					tier
					trialEnded
					trialEndYear
					trialEndMonth
					trialEndDay
					allSavedReports(first: 100000) {
						edges {
							node {
								id
								name
								type
							}
						}
					}
				}
				projects(first: 1000000) {
					edges {
						node {
							id
							companyProjectId
							budgetType
							sprintTimeBox
							isInProjectGroup
							useBaseline
						}
					}
				}
				tasks(first: 1000000) {
					edges {
						node {
							id
							companyTaskId
						}
					}
				}
			}
		`,
	})
);
